import PropTypes from 'prop-types';
import React, { useContext, useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { LoadingContext, M_StateActions, MTable } from 'src/M_Components';

export default function ApiKeyConfig({ roles, onCreateTab, toastMessages }) {
  const ctx = useContext(LoadingContext);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [refresh, setRefresh] = useState(false);

  // Remove context items that are not needed
  const removeContextItems = useMemo(() => ['Print', 'Copy'], []);

  // Crud route for ApiKeyConfig
  const CRUD_ROUTE = '/home/ApiKeyConfig';

  // ============================================================================================================
  // Opens the ApiKey CRUD tab based on the operation type
  // ============================================================================================================
  const openApiKeyCrud = useCallback(
    (operation, data) => {
      dispatch(M_StateActions.addM_State({ key: 'operation', value: operation }));

      if (['Edit', 'Copy', 'View'].includes(operation)) {
        if (!data) {
          toastMessages('error', '', t('SelectedLine'));
          return;
        }
        dispatch(M_StateActions.addM_State({ key: 'ApiKeyLine', value: data }));
      }

      const tabLabel =
        operation === 'Create'
          ? t('LabelNewApiKey')
          : operation === 'View'
          ? `${t('LabelViewApiKey')} "${data?.Name ?? ''}"`
          : `${t('LabelEditApiKey')} "${data?.Name ?? ''}"`;

      onCreateTab(CRUD_ROUTE, tabLabel);
    },
    [dispatch, onCreateTab, t, toastMessages]
  );

  // ============================================================================================================
  // Define functions to replace default actions in the MTable
  // ============================================================================================================
  const replaceFunctions = useMemo(
    () =>
      new Map([
        ['View', (row) => openApiKeyCrud('View', row)],
        ['Create', () => openApiKeyCrud('Create')],
        ['Edit', (row) => openApiKeyCrud('Edit', row)],
      ]),
    [openApiKeyCrud]
  );

  return (
    <div className="divGlobalSettings">
      <div className="subTitleSettings">
        <div>
          <div style={{ color: ctx.secondaryColor }}>
            {t('ApiKeys')}
          </div>
          <div className="labelSubtitleSettings">
            {t('InfoApiKeys')}
          </div>
        </div>
      </div>

      <MTable
        // Dynamic table component for displaying API keys
        filtersOnTable
        entityName={'ApiKeys'}
        roleName={'Accounts'}
        rolePermissions={roles} // User roles for permission management
        removeContextItems={removeContextItems}
        refresh={refresh}
        setRefresh={setRefresh}
        useSessionStorage={false}
        replaceFunctions={replaceFunctions}
      />
    </div>
  );
}

ApiKeyConfig.propTypes = {
  roles: PropTypes.array,
  onCreateTab: PropTypes.func,
  toastMessages: PropTypes.func
};
